import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  MessageSquareIcon, 
  XIcon, 
  Send, 
  LoaderIcon,
  UserIcon,
  ShieldCheckIcon,
  PowerIcon
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getApiEndpoint } from "@/lib/api";
import { getToken } from "@/lib/auth-storage";

interface ChatMessage {
  id: number;
  content: string;
  sender_name: string;
  sender_role: string;
  is_from_admin: boolean;
  created_at: string;
}

interface ChatWidgetProps {
  chatId: number | null;
  onClose: () => void;
  onChatEnded?: () => void;
}

const ChatWidget = ({ chatId, onClose, onChatEnded }: ChatWidgetProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [ending, setEnding] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!chatId) return;

    const fetchMessages = async () => {
      try {
        setLoading(true);
        const token = getToken();
        const response = await fetch(getApiEndpoint(`/api/chat/${chatId}/messages`), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setMessages(data.messages || []);
        }
      } catch (error) {
        console.error("Erro ao buscar mensagens:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
    
    // Polling para novas mensagens a cada 2 segundos
    const interval = setInterval(fetchMessages, 2000);
    return () => clearInterval(interval);
  }, [chatId]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !chatId || sending) return;

    try {
      setSending(true);
      const token = getToken();
      const response = await fetch(getApiEndpoint(`/api/chat/${chatId}/messages`), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ content: newMessage }),
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(prev => [...prev, data.chat_message]);
        setNewMessage("");
      } else {
        const errorData = await response.json();
        toast({
          title: "Erro",
          description: errorData.error || "Não foi possível enviar a mensagem",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível enviar a mensagem",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleEndChat = async () => {
    if (!chatId || ending) return;

    if (!confirm("Tem certeza que deseja encerrar este chat? O chat será fechado permanentemente.")) {
      return;
    }

    try {
      setEnding(true);
      const token = getToken();
      const response = await fetch(getApiEndpoint(`/api/chat/${chatId}/close`), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast({
          title: "Chat encerrado",
          description: "O chat foi encerrado com sucesso.",
        });
        if (onChatEnded) {
          onChatEnded();
        }
        onClose();
      } else {
        const errorData = await response.json();
        toast({
          title: "Erro",
          description: errorData.error || "Não foi possível encerrar o chat",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível encerrar o chat",
        variant: "destructive",
      });
    } finally {
      setEnding(false);
    }
  };

  if (!chatId) return null;

  return (
    <Card className="fixed bottom-24 right-6 w-96 h-[500px] flex flex-col shadow-2xl z-50">
      <CardHeader className="flex flex-row items-center justify-between pb-3 border-b">
        <CardTitle className="text-lg flex items-center gap-2">
          <MessageSquareIcon className="h-5 w-5 text-blue-600" />
          Chat de Suporte
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleEndChat}
            disabled={ending}
            className="h-8 px-3 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
            title="Encerrar chat"
          >
            <PowerIcon className="h-3 w-3 mr-1" />
            Encerrar
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
            title="Fechar janela"
          >
            <XIcon className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col p-0">
        <ScrollArea className="flex-1 p-4">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <LoaderIcon className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-500 text-sm">
              Nenhuma mensagem ainda. Digite uma mensagem para começar.
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.is_from_admin ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg p-3 ${
                      message.is_from_admin
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 text-gray-900"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {message.is_from_admin ? (
                        <ShieldCheckIcon className="h-3 w-3" />
                      ) : (
                        <UserIcon className="h-3 w-3" />
                      )}
                      <span className="text-xs font-medium opacity-80">
                        {message.sender_name}
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    <p className="text-xs opacity-70 mt-1">
                      {new Date(message.created_at).toLocaleTimeString("pt-BR", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>
        <div className="border-t p-3">
          <div className="flex gap-2">
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Digite sua mensagem..."
              disabled={sending}
              className="flex-1"
            />
            <Button
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || sending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sending ? (
                <LoaderIcon className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ChatWidget;

